import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getNextApiUrl } from '@/utils/next-api';
import { getAppVersion } from '@/utils/application';
import { getUTMStringFromLocalStorage } from '@/containers/AnalyticsContainer/UTMContainer/UTMContainer.utils';
import { handleAutoLogout } from './handleAutoLogout';
import { TApiError } from './ReactQuery';

const isSSR: boolean = typeof window === 'undefined';
const lastVersion = getAppVersion();

// Add a refresh token state tracking mechanism
let isRefreshingToken = false;
let refreshTokenPromise: Promise<any> | null = null;
const pendingRequests: Array<{
  resolve: (__value?: any) => void;
  reject: (__reason?: any) => void;
  config: any;
}> = [];

const axiosInstance: AxiosInstance = axios.create({
  timeout: 150000,
  withCredentials: false,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const utmString = isSSR ? null : getUTMStringFromLocalStorage();
    return Object.assign(config, {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Appname-Versionname': lastVersion,
        Pragma: 'no-cache',
        Expires: '0',
        ...(utmString ? { 'utm-params': utmString } : {}),
        ...(config?.headers || {}),
      },
    });
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error?.config;

    // Check if there's an Authorization header to determine if the user was authenticated (Token stored in cookie)
    const hasAccessToken = Boolean(
      error?.response?.config?.headers?.Authorization
    );

    const isSessionApi = originalConfig?.url?.includes('/auth/session');

    // Determine if we should skip the token refresh process
    const shouldSkipTokenRefresh =
      error?.response?.status !== 401 ||
      Boolean(originalConfig?._retry) ||
      (!hasAccessToken && !isSessionApi);

    // If not 401 error or already retried, just reject
    if (shouldSkipTokenRefresh) {
      return Promise.reject(error);
    }

    // Mark this request as retried to prevent infinite loops
    originalConfig._retry = true;

    // If the original request was for refreshing token or session (session api call refresh token itself), reject immediately and logout
    const shouldLogoutImmediately =
      originalConfig?.url?.includes('/auth/refresh-token') || isSessionApi;

    if (shouldLogoutImmediately) {
      // Logout user as refresh token is invalid, skipping account PKCE logout
      handleAutoLogout();
      return Promise.reject(error);
    }

    // If already refreshing token, queue this request
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve,
          reject,
          config: originalConfig,
        });
      });
    }

    // Set refreshing flag to true
    isRefreshingToken = true;

    try {
      // Create a refresh token promise if it doesn't exist
      if (!refreshTokenPromise) {
        refreshTokenPromise = axios.post(
          getNextApiUrl('auth/refresh-token'),
          {},
          {
            withCredentials: true,
          }
        );
      }

      // Wait for the refresh token response
      const response = await refreshTokenPromise;

      // If refresh successful, process all pending requests
      if (response.status === 200) {
        // Process all pending requests
        pendingRequests.forEach(({ resolve, reject, config }) => {
          axiosInstance
            .request(config)
            .then((response) => resolve(response))
            .catch((err) => reject(err));
        });

        // Clear the queue
        pendingRequests.length = 0;

        if (!isSSR) {
          window.location.reload(); // Reload page to trigger AuthSessionProvider
        }
        return Promise.reject(error); // Still reject to prevent the original promise from resolving
      }

      return Promise.reject(error);
    } catch (refreshError: any) {
      // If refresh token request fails with 401, logout user skipping account PKCE logout
      if (refreshError?.response?.status === 401) {
        handleAutoLogout();
      }

      // Reject all pending requests
      pendingRequests.forEach(({ reject }) => {
        reject(refreshError);
      });

      // Clear the queue
      pendingRequests.length = 0;

      return Promise.reject(refreshError);
    } finally {
      // Reset refresh token state
      isRefreshingToken = false;
      refreshTokenPromise = null;
    }
  }
);

export const request = async ({
  resolveInterceptor = (response) => response?.data?.data,
  rejectInterceptor = (error) => Promise.reject(error),
  ...config
}: AxiosRequestConfig & { shouldReturnDetailedError?: boolean } & {
  resolveInterceptor?: (__response: AxiosResponse) => any;
  rejectInterceptor?: (__error: TApiError) => any;
}) => {
  try {
    const response = await axiosInstance.request(config);
    return resolveInterceptor(response);
  } catch (error: any) {
    let message: string = !error?.response
      ? 'ارتباط با سرور برقرار نشد.'
      : error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        error?.response?.statusText;

    let code: string | number =
      `${error?.response?.data?.error?.code || ''}` ||
      error?.response?.status ||
      error?.code;

    // We only handle non-401 errors here as 401s are handled by the interceptor above
    // But we still format the error message for consistency
    if (error?.response?.status?.toString() === '401') {
      code = error?.response?.status;
      message = message || 'ابتدا باید وارد سایت شوید.';
    }

    const detailedError = {
      serviceName: error?.request?.responseURL,
      status: error?.status,
    };

    return rejectInterceptor({
      code,
      message: message || 'خطای ناشناخته رخ داده است.',
      displayMode: error?.response?.data?.error?.displayMode,
      ...(config?.shouldReturnDetailedError ? detailedError : {}),
    });
  }
};

export default request;
